import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "sonner";
import {
  Search, RefreshCw, Download, FileSpreadsheet, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight, ArrowUpDown, Loader2, Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FilterSelect } from "@/components/common/FilterSelect";
import { reportsApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ReportsShell } from "@/pages/reports/ReportsShell";
import { LeadReportDetailSheet } from "@/pages/reports/LeadReportDetailSheet";

const PAGE_SIZE_KEY = "sellix.leadReports.pageSize";
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const LEAD_TYPE_OPTIONS = [
  { value: "all", label: "Lead type: All" },
  { value: "inquiry", label: "Inquiry Lead" },
  { value: "mature", label: "Mature Lead" },
];

const SIGNAL_OPTIONS = [
  { value: "all", label: "Source: All" },
  { value: "rnc", label: "RNC" },
  { value: "abm", label: "ABM List" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "Status: All" },
  { value: "qualified_inquiry", label: "Qualified as Inquiry Lead" },
  { value: "qualified_mature", label: "Qualified as Mature Lead" },
];

const EMAIL_OPTIONS = [
  { value: "all", label: "Email: All" },
  { value: "sent", label: "Sent" },
  { value: "failed", label: "Failed" },
  { value: "pending", label: "Pending" },
  { value: "queued", label: "Queued" },
];

const DATE_OPTIONS = [
  { value: "all", label: "Qualified: All time" },
  { value: "today", label: "Today" },
  { value: "last_7_days", label: "Last 7 days" },
  { value: "last_30_days", label: "Last 30 days" },
  { value: "custom", label: "Custom range" },
];

function readStoredPageSize() {
  try {
    const n = Number(localStorage.getItem(PAGE_SIZE_KEY));
    return PAGE_SIZE_OPTIONS.includes(n) ? n : 25;
  } catch {
    return 25;
  }
}

function emailBadgeClass(status) {
  if (status === "sent") return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  if (status === "failed") return "bg-red-50 text-red-700 ring-red-200";
  return "bg-amber-50 text-amber-800 ring-amber-200";
}

function StatCard({ label, value, testId }) {
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm"
      data-testid={testId}
    >
      <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-slate-900 tabular-nums">{value ?? 0}</div>
    </div>
  );
}

StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  testId: PropTypes.string,
};

export default function LeadReportsPage() {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [leadType, setLeadType] = useState("all");
  const [status, setStatus] = useState("all");
  const [signalSource, setSignalSource] = useState("all");
  const [assignedUserId, setAssignedUserId] = useState("all");
  const [emailStatus, setEmailStatus] = useState("all");
  const [datePreset, setDatePreset] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortKey, setSortKey] = useState("qualification_date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(readStoredPageSize);
  const [users, setUsers] = useState([]);
  const [exporting, setExporting] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const filterParams = useMemo(() => ({
    q: q || undefined,
    lead_type: leadType,
    status,
    signal_source: signalSource,
    assigned_user_id: assignedUserId === "all" ? undefined : assignedUserId,
    email_status: emailStatus,
    date_preset: datePreset,
    date_from: datePreset === "custom" ? dateFrom || undefined : undefined,
    date_to: datePreset === "custom" ? dateTo || undefined : undefined,
    sort: sortKey,
    order: sortOrder,
  }), [
    q, leadType, status, signalSource, assignedUserId, emailStatus,
    datePreset, dateFrom, dateTo, sortKey, sortOrder,
  ]);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await reportsApi.listLeads({
        ...filterParams,
        limit: pageSize,
        offset: page * pageSize,
      });
      setState({ loading: false, error: null, data });
    } catch (e) {
      setState({ loading: false, error: e.message || "Failed to load lead reports", data: null });
    }
  }, [filterParams, page, pageSize]);

  useEffect(() => { queueMicrotask(load); }, [load]);

  useEffect(() => {
    let cancelled = false;
    reportsApi.listUsers()
      .then((data) => {
        if (!cancelled) setUsers(data.users || []);
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => {
      const next = qInput.trim();
      setQ((prev) => {
        if (prev === next) return prev;
        setPage(0);
        return next;
      });
    }, 300);
    return () => window.clearTimeout(t);
  }, [qInput]);

  const userOptions = useMemo(() => ([
    { value: "all", label: "Assigned: All users" },
    ...users.map((u) => ({ value: u.id, label: u.name || u.email })),
  ]), [users]);

  const leads = state.data?.leads || [];
  const total = state.data?.total ?? 0;
  const stats = state.data?.stats || {
    total_qualified: 0,
    inquiry_leads: 0,
    mature_leads: 0,
    emails_sent: 0,
    emails_failed: 0,
  };
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, page * pageSize + leads.length);

  const onSort = (key) => {
    if (sortKey === key) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder(key === "opportunity_name" || key === "company" || key === "assigned_user" ? "asc" : "desc");
    }
    setPage(0);
  };

  const resetPage = (fn) => (value) => {
    fn(value);
    setPage(0);
  };

  const onExport = async (kind) => {
    setExporting(kind);
    try {
      if (kind === "excel") await reportsApi.exportExcel(filterParams);
      else await reportsApi.exportPdf(filterParams);
      toast.success(kind === "excel" ? "Excel export downloaded" : "PDF export downloaded");
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally {
      setExporting(null);
    }
  };

  return (
    <ReportsShell
      title="Lead Reports"
      desc="All opportunities qualified as Inquiry or Mature leads across your workspace."
    >
      <div className="p-4 sm:p-6 space-y-4" data-testid="lead-reports-page">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
          <StatCard label="Total Qualified Leads" value={stats.total_qualified} testId="stat-total-qualified" />
          <StatCard label="Inquiry Leads" value={stats.inquiry_leads} testId="stat-inquiry" />
          <StatCard label="Mature Leads" value={stats.mature_leads} testId="stat-mature" />
          <StatCard label="Emails Sent" value={stats.emails_sent} testId="stat-emails-sent" />
          <StatCard label="Emails Failed" value={stats.emails_failed} testId="stat-emails-failed" />
        </div>

        <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                placeholder="Search name, company, email, phone, ID…"
                className="pl-8 h-9"
                data-testid="lead-reports-search"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={load}
              disabled={state.loading}
              data-testid="lead-reports-refresh"
            >
              <RefreshCw size={14} className={cn(state.loading && "animate-spin")} />
              Refresh
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onExport("excel")}
              disabled={!!exporting}
              data-testid="lead-reports-export-excel"
            >
              {exporting === "excel" ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
              Export Excel
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onExport("pdf")}
              disabled={!!exporting}
              data-testid="lead-reports-export-pdf"
            >
              {exporting === "pdf" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Export PDF
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <FilterSelect
              value={leadType}
              onChange={resetPage(setLeadType)}
              options={LEAD_TYPE_OPTIONS}
              aria-label="Lead type"
              data-testid="filter-lead-type"
            />
            <FilterSelect
              value={signalSource}
              onChange={resetPage(setSignalSource)}
              options={SIGNAL_OPTIONS}
              aria-label="Signal source"
              data-testid="filter-signal-source"
            />
            <FilterSelect
              value={assignedUserId}
              onChange={resetPage(setAssignedUserId)}
              options={userOptions}
              aria-label="Assigned user"
              data-testid="filter-assigned-user"
            />
            <FilterSelect
              value={datePreset}
              onChange={resetPage(setDatePreset)}
              options={DATE_OPTIONS}
              aria-label="Qualification date"
              data-testid="filter-date-preset"
            />
            <FilterSelect
              value={status}
              onChange={resetPage(setStatus)}
              options={STATUS_OPTIONS}
              aria-label="Status"
              data-testid="filter-status"
            />
            <FilterSelect
              value={emailStatus}
              onChange={resetPage(setEmailStatus)}
              options={EMAIL_OPTIONS}
              aria-label="Email notification status"
              data-testid="filter-email-status"
            />
          </div>

          {datePreset === "custom" && (
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-[12px] text-slate-500">From</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
                className="h-9 w-auto"
                data-testid="filter-date-from"
              />
              <label className="text-[12px] text-slate-500">To</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
                className="h-9 w-auto"
                data-testid="filter-date-to"
              />
            </div>
          )}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13px]" data-testid="lead-reports-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2.5 font-medium">Opportunity ID</th>
                  <th className="px-3 py-2.5 font-medium">
                    <button type="button" className="inline-flex items-center gap-1 hover:text-slate-800" onClick={() => onSort("opportunity_name")}>
                      Opportunity Name
                      <ArrowUpDown size={12} className={cn(sortKey === "opportunity_name" && "text-indigo-600")} />
                    </button>
                  </th>
                  <th className="px-3 py-2.5 font-medium">Contact</th>
                  <th className="px-3 py-2.5 font-medium">
                    <button type="button" className="inline-flex items-center gap-1 hover:text-slate-800" onClick={() => onSort("company")}>
                      Company
                      <ArrowUpDown size={12} className={cn(sortKey === "company" && "text-indigo-600")} />
                    </button>
                  </th>
                  <th className="px-3 py-2.5 font-medium">Source</th>
                  <th className="px-3 py-2.5 font-medium">Lead Type</th>
                  <th className="px-3 py-2.5 font-medium">Status</th>
                  <th className="px-3 py-2.5 font-medium">
                    <button type="button" className="inline-flex items-center gap-1 hover:text-slate-800" onClick={() => onSort("assigned_user")}>
                      Assigned
                      <ArrowUpDown size={12} className={cn(sortKey === "assigned_user" && "text-indigo-600")} />
                    </button>
                  </th>
                  <th className="px-3 py-2.5 font-medium">
                    <button type="button" className="inline-flex items-center gap-1 hover:text-slate-800" onClick={() => onSort("qualification_date")}>
                      Qualified
                      <ArrowUpDown size={12} className={cn(sortKey === "qualification_date" && "text-indigo-600")} />
                    </button>
                  </th>
                  <th className="px-3 py-2.5 font-medium">Email</th>
                  <th className="px-3 py-2.5 font-medium">
                    <button type="button" className="inline-flex items-center gap-1 hover:text-slate-800" onClick={() => onSort("last_updated")}>
                      Updated
                      <ArrowUpDown size={12} className={cn(sortKey === "last_updated" && "text-indigo-600")} />
                    </button>
                  </th>
                  <th className="px-3 py-2.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {state.loading && leads.length === 0 && (
                  <tr>
                    <td colSpan={12} className="px-3 py-12 text-center text-slate-400">
                      <Loader2 className="inline animate-spin mr-2" size={16} />
                      Loading lead reports…
                    </td>
                  </tr>
                )}
                {!state.loading && state.error && (
                  <tr>
                    <td colSpan={12} className="px-3 py-12 text-center text-red-600">{state.error}</td>
                  </tr>
                )}
                {!state.loading && !state.error && leads.length === 0 && (
                  <tr>
                    <td colSpan={12} className="px-3 py-12 text-center text-slate-500">
                      No qualified leads match your filters.
                    </td>
                  </tr>
                )}
                {leads.map((lead) => (
                  <tr
                    key={lead.id}
                    className="border-b border-slate-100 hover:bg-slate-50/80"
                    data-testid={`lead-report-row-${lead.id}`}
                  >
                    <td className="px-3 py-2.5 font-mono text-[11px] text-slate-500 max-w-[120px] truncate" title={lead.opportunity_id}>
                      {String(lead.opportunity_id || "").slice(0, 8)}…
                    </td>
                    <td className="px-3 py-2.5 font-medium text-slate-900 max-w-[160px] truncate">{lead.opportunity_name}</td>
                    <td className="px-3 py-2.5">
                      <div className="font-medium text-slate-800">{lead.contact_name}</div>
                      <div className="text-[11px] text-slate-500 truncate max-w-[160px]">{lead.email || lead.phone || "—"}</div>
                    </td>
                    <td className="px-3 py-2.5 max-w-[140px] truncate">{lead.company}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">{lead.signal_source}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">{lead.lead_type}</td>
                    <td className="px-3 py-2.5 max-w-[160px] text-[12px] text-slate-700">{lead.current_status}</td>
                    <td className="px-3 py-2.5 max-w-[120px] truncate">{lead.assigned_user}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{formatDateTime(lead.qualification_date)}</td>
                    <td className="px-3 py-2.5">
                      <span className={cn(
                        "inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset",
                        emailBadgeClass(lead.email_sent_status),
                      )}
                      >
                        {lead.email_sent_label}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{formatDateTime(lead.last_updated)}</td>
                    <td className="px-3 py-2.5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => setDetailId(lead.id)}
                        data-testid={`lead-report-view-${lead.id}`}
                      >
                        <Eye size={14} />
                        View Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-3 py-2.5 bg-slate-50/50">
            <div className="text-[12px] text-slate-500">
              {total === 0 ? "0 records" : `Showing ${from}–${to} of ${total}`}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-[12px] text-slate-500" htmlFor="lead-reports-page-size">Rows</label>
              <select
                id="lead-reports-page-size"
                className="h-8 rounded-md border border-slate-200 bg-white px-2 text-[12px]"
                value={pageSize}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  setPageSize(n);
                  setPage(0);
                  try { localStorage.setItem(PAGE_SIZE_KEY, String(n)); } catch { /* ignore */ }
                }}
                data-testid="lead-reports-page-size"
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <div className="flex items-center gap-1">
                <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={page <= 0} onClick={() => setPage(0)} aria-label="First page">
                  <ChevronsLeft size={14} />
                </Button>
                <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))} aria-label="Previous page">
                  <ChevronLeft size={14} />
                </Button>
                <span className="text-[12px] text-slate-600 px-2 tabular-nums">
                  Page {page + 1} / {pageCount}
                </span>
                <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={page + 1 >= pageCount} onClick={() => setPage((p) => p + 1)} aria-label="Next page">
                  <ChevronRight size={14} />
                </Button>
                <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={page + 1 >= pageCount} onClick={() => setPage(pageCount - 1)} aria-label="Last page">
                  <ChevronsRight size={14} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <LeadReportDetailSheet
        enrollmentId={detailId}
        open={Boolean(detailId)}
        onOpenChange={(open) => { if (!open) setDetailId(null); }}
      />
    </ReportsShell>
  );
}
